import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";
import { validateToken } from "../utils/api";

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which row is being deleted
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importDialog, setImportDialog] = useState({
    open: false,
    progress: 0,
    message: "",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null,
  });

  useEffect(() => {
    const fetchUser = async () => {
      const fetchedUser = await validateToken();
      setUser(fetchedUser);
    };

    fetchUser();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const fetchData = async (search = "") => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_HOST_API}/bds/search`, {
        params: {
          search,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      setData(response.data.data); // Dữ liệu từ API
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showSnackbar("Lỗi khi tải dữ liệu", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (params) => {
    const id = params.row.id; // Sử dụng id từ model đã sửa
    try {
      const response = await axios.get(`${process.env.REACT_APP_HOST_API}/bds/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      const detailData = response.data;
      navigate(`/detail/${id}`);
    } catch (error) {
      console.error("Failed to fetch detail data:", error);
      showSnackbar("Lỗi khi tải chi tiết", "error");
    }
  };

  const handleDeleteClick = (event, item) => {
    event.stopPropagation(); // Prevent row click when clicking delete button
    setDeleteDialog({
      open: true,
      item: item,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.item) return;
    const token = `${localStorage.getItem("accessToken")}`;
    console.log("token", token);

    const id = deleteDialog.item.id;

    try {
      setDeleting(id);

      await axios.put(
        `${process.env.REACT_APP_HOST_API}/bds/delete/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // Remove item from local state
      setData((prevData) => prevData.filter((item) => item.id !== id));

      showSnackbar("Xóa bản ghi thành công", "success");
      setDeleteDialog({ open: false, item: null });
    } catch (error) {
      console.error("Failed to delete item:", error);

      let errorMessage = "Lỗi khi xóa bản ghi";
      if (error.response) {
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, item: null });
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getFileFromUser = () => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls";
      input.multiple = false;

      input.addEventListener("change", (event) => {
        const file = event.target.files[0];
        if (file) {
          // Validate file type
          const validTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
          if (!validTypes.includes(file.type)) {
            reject(new Error("Vui lòng chọn file Excel (.xlsx hoặc .xls)"));
            return;
          }

          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            reject(new Error("File quá lớn. Vui lòng chọn file nhỏ hơn 10MB"));
            return;
          }

          resolve(file);
        } else {
          reject(new Error("Không có file nào được chọn"));
        }
      });

      input.addEventListener("cancel", () => {
        reject(new Error("Đã hủy chọn file"));
      });

      input.click();
    });
  };

  const func_importExcel = async () => {
    if (importing) return; // Prevent double clicks

    try {
      setImporting(true);
      setImportDialog({
        open: true,
        progress: 10,
        message: "Đang chọn file...",
      });

      // Get file from user
      const file = await getFileFromUser();

      setImportDialog((prev) => ({
        ...prev,
        progress: 30,
        message: "Đang tải file lên...",
      }));

      // Create FormData
      const formData = new FormData();
      formData.append("files", file);

      // Call API to import data
      const response = await axios.post(`${process.env.REACT_APP_HOST_API}/bds/uploads`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(30 + (progressEvent.loaded * 50) / progressEvent.total);
          setImportDialog((prev) => ({
            ...prev,
            progress: percentCompleted,
            message: "Đang xử lý dữ liệu...",
          }));
        },
      });

      setImportDialog((prev) => ({
        ...prev,
        progress: 90,
        message: "Hoàn thành xử lý...",
      }));

      // Handle success
      if (response.status === 200) {
        setImportDialog((prev) => ({
          ...prev,
          progress: 100,
          message: "Import thành công!",
        }));

        setTimeout(() => {
          setImportDialog({ open: false, progress: 0, message: "" });
          showSnackbar(`Import thành công `, "success");
          // Refresh data
          fetchData(searchTerm);
        }, 1500);
      } else {
        throw new Error(response.data.message || "Import thất bại");
      }
    } catch (error) {
      setImportDialog({ open: false, progress: 0, message: "" });

      let errorMessage = "Lỗi khi import dữ liệu";
      if (error.response) {
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("Import error:", error);
      showSnackbar(errorMessage, "error");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchData(searchTerm);
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchData(searchTerm);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "ten_du_an", headerName: "Tên dự án", width: 250 },
    { field: "chu_dau_tu", headerName: "Chủ đầu tư", width: 150 },
    { field: "loai_hinh_bds", headerName: "Loại hình BĐS", width: 120 },
    { field: "hien_trang", headerName: "Hiện trạng", width: 150 },
    { field: "dia_chi_gop", headerName: "Địa chỉ gộp", width: 300 },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          size="small"
          onClick={(event) => handleDeleteClick(event, params.row)}
          disabled={deleting === params.row.id}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          {deleting === params.row.id ? <CircularProgress size={16} color="error" /> : <DeleteIcon fontSize="small" />}
        </IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" gutterBottom>
          PMC Bất động sản
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" color="primary" style={{ width: "120px", height: "100%" }} onClick={() => navigate(`/new`)} sx={{ mr: 2 }}>
            Thêm mới
          </Button>
          {user?.isAction == 1 && (
            <Button
              variant="contained"
              color="secondary"
              style={{ width: "150px", height: "100%" }}
              onClick={func_importExcel}
              disabled={importing}
              sx={{ mr: 2 }}
            >
              {importing ? <CircularProgress size={20} /> : "Import Excel"}
            </Button>
          )}
          <Button variant="contained" color="error" onClick={handleLogout}>
            Đăng xuất
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <TextField
          label="Tìm kiếm theo tên dự án, chủ đầu tư, hiện trạng"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button
          variant="contained"
          color="primary"
          style={{ width: "120px", height: "100%" }}
          onClick={handleSearch}
          disabled={loading}
          sx={{ ml: 2 }}
        >
          {loading ? <CircularProgress size={20} /> : "Tìm kiếm"}
        </Button>
      </Box>

      <Box sx={{ height: 600, width: "100%", mt: 1 }}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          pagination={false}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          sx={{
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>Bạn có chắc chắn muốn xóa bản ghi "{deleteDialog.item?.ten_du_an}" không?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Hủy
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting === deleteDialog.item?.id}>
            {deleting === deleteDialog.item?.id ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Đang xóa...
              </>
            ) : (
              "Xóa"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Progress Dialog */}
      <Dialog open={importDialog.open} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle>Đang import dữ liệu...</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {importDialog.message}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={importDialog.progress} sx={{ height: 8, borderRadius: 4 }} />
          <Box sx={{ mt: 1, textAlign: "center" }}>
            <Typography variant="body2">{importDialog.progress}%</Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Home;
